import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Snackbar,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  Grid,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import FinancialControlService, {
  FinancialProject,
  ExpenseEntry,
  ExpenseCategory,
} from '../../services/financialControlService';
import { format } from 'date-fns';

const FinancialControlExpenses: React.FC = () => {
  const [projects, setProjects] = useState<FinancialProject[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('all');
  const [expenses, setExpenses] = useState<ExpenseEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<ExpenseEntry | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [expenseForm, setExpenseForm] = useState({
    project_id: '',
    category_id: '',
    description: '',
    amount: '',
    expense_date: format(new Date(), 'yyyy-MM-dd'),
    payment_method: '',
    is_recurring: false,
    recurring_frequency: '',
    receipt_url: '',
    notes: '',
  });

  useEffect(() => {
    loadProjects();
    loadCategories();
  }, []);

  useEffect(() => {
    if (projects.length > 0) {
      loadExpenses();
    }
  }, [selectedProject, projects]);

  const loadProjects = async () => {
    try {
      const response = await FinancialControlService.listProjects();
      if (response.success && response.data) {
        setProjects(response.data);
        if (response.data.length > 0 && !expenseForm.project_id) {
          setExpenseForm({ ...expenseForm, project_id: response.data[0].id });
        }
      }
    } catch (error) {
      console.error('Erro ao carregar projetos:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await FinancialControlService.listExpenseCategories();
      if (response.success && response.data) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const loadExpenses = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (selectedProject !== 'all') {
        params.project_id = selectedProject;
      }

      const response = await FinancialControlService.listExpenses(params);
      if (response.success && response.data) {
        setExpenses(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar despesas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncFacebookAds = async () => {
    if (selectedProject === 'all') {
      setSnackbar({ open: true, message: 'Selecione um projeto primeiro', severity: 'error' });
      return;
    }

    try {
      const response = await FinancialControlService.syncFacebookAds({
        project_id: selectedProject,
      });

      if (response.success) {
        setSnackbar({ 
          open: true, 
          message: `${response.data?.synced_count || 0} despesas sincronizadas do Facebook Ads`, 
          severity: 'success' 
        });
        loadExpenses();
      } else {
        setSnackbar({ open: true, message: response.error || 'Erro ao sincronizar', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Erro ao sincronizar', severity: 'error' });
    }
  };

  const handleOpenDialog = (expense?: ExpenseEntry) => {
    if (expense) {
      setEditingExpense(expense);
      setExpenseForm({
        project_id: expense.project_id,
        category_id: expense.category_id || '',
        description: expense.description,
        amount: expense.amount.toString(),
        expense_date: expense.expense_date,
        payment_method: expense.payment_method || '',
        is_recurring: expense.is_recurring,
        recurring_frequency: expense.recurring_frequency || '',
        receipt_url: expense.receipt_url || '',
        notes: expense.notes || '',
      });
    } else {
      setEditingExpense(null);
      setExpenseForm({
        project_id: projects[0]?.id || '',
        category_id: '',
        description: '',
        amount: '',
        expense_date: format(new Date(), 'yyyy-MM-dd'),
        payment_method: '',
        is_recurring: false,
        recurring_frequency: '',
        receipt_url: '',
        notes: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingExpense(null);
  };

  const handleSave = async () => {
    if (!expenseForm.project_id || !expenseForm.description || !expenseForm.amount || !expenseForm.expense_date) {
      setSnackbar({ open: true, message: 'Preencha todos os campos obrigatórios', severity: 'error' });
      return;
    }

    try {
      const expenseData: any = {
        project_id: expenseForm.project_id,
        category_id: expenseForm.category_id || undefined,
        description: expenseForm.description,
        amount: parseFloat(expenseForm.amount),
        expense_date: expenseForm.expense_date,
        payment_method: expenseForm.payment_method || undefined,
        is_recurring: expenseForm.is_recurring,
        recurring_frequency: expenseForm.recurring_frequency || undefined,
        receipt_url: expenseForm.receipt_url || undefined,
        notes: expenseForm.notes || undefined,
      };

      let response;
      if (editingExpense) {
        response = await FinancialControlService.updateExpense(editingExpense.id, expenseData);
      } else {
        response = await FinancialControlService.createExpense(expenseData);
      }

      if (response.success) {
        setSnackbar({ 
          open: true, 
          message: editingExpense ? 'Despesa atualizada com sucesso' : 'Despesa criada com sucesso', 
          severity: 'success' 
        });
        handleCloseDialog();
        loadExpenses();
      } else {
        setSnackbar({ open: true, message: response.error || 'Erro ao salvar despesa', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Erro ao salvar despesa', severity: 'error' });
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja deletar esta despesa?')) return;

    try {
      const response = await FinancialControlService.deleteExpense(id);
      if (response.success) {
        setSnackbar({ open: true, message: 'Despesa deletada com sucesso', severity: 'success' });
        loadExpenses();
      } else {
        setSnackbar({ open: true, message: response.error || 'Erro ao deletar despesa', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Erro ao deletar despesa', severity: 'error' });
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatDate = (date: string) => {
    return format(new Date(date), 'dd/MM/yyyy');
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Despesas
        </Typography>
        <Stack direction="row" spacing={1}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Projeto</InputLabel>
            <Select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              label="Projeto"
            >
              <MenuItem value="all">Todos os Projetos</MenuItem>
              {projects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button 
            variant="outlined" 
            startIcon={<SyncIcon />} 
            onClick={handleSyncFacebookAds}
            disabled={selectedProject === 'all'}
            color="info"
          >
            Sincronizar Facebook Ads
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()} color="primary">
            Nova Despesa
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadExpenses} disabled={loading}>
            Atualizar
          </Button>
        </Stack>
      </Box>

      {/* Tabela */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Data</TableCell>
              <TableCell>Descrição</TableCell>
              <TableCell>Categoria</TableCell>
              <TableCell align="right">Valor</TableCell>
              <TableCell>Método de Pagamento</TableCell>
              <TableCell align="center">Recorrente</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {expenses.map((expense) => (
              <TableRow key={expense.id} hover>
                <TableCell>{formatDate(expense.expense_date)}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {expense.description}
                  </Typography>
                  {expense.notes && (
                    <Typography variant="caption" color="text.secondary">
                      {expense.notes}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {expense.expense_categories ? (
                    <Chip 
                      label={expense.expense_categories.name} 
                      size="small" 
                      sx={{ bgcolor: expense.expense_categories.color, color: '#000000' }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">-</Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                    {formatCurrency(expense.amount)}
                  </Typography>
                </TableCell>
                <TableCell>{expense.payment_method || '-'}</TableCell>
                <TableCell align="center">
                  {expense.is_recurring && (
                    <Chip label={expense.recurring_frequency || 'Recorrente'} size="small" color="info" />
                  )}
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleOpenDialog(expense)} color="primary">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Deletar">
                      <IconButton size="small" onClick={() => handleDelete(expense.id)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {expenses.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary">Nenhuma despesa encontrada</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de Criar/Editar Despesa */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingExpense ? 'Editar Despesa' : 'Nova Despesa'}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Projeto</InputLabel>
              <Select
                value={expenseForm.project_id}
                onChange={(e) => setExpenseForm({ ...expenseForm, project_id: e.target.value })}
                label="Projeto"
              >
                {projects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Categoria</InputLabel>
              <Select
                value={expenseForm.category_id}
                onChange={(e) => setExpenseForm({ ...expenseForm, category_id: e.target.value })}
                label="Categoria"
              >
                <MenuItem value="">Sem categoria</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Descrição"
              fullWidth
              required
              value={expenseForm.description}
              onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
              placeholder="Ex: Assinatura mensal do CRM"
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Valor"
                  fullWidth
                  required
                  type="number"
                  value={expenseForm.amount}
                  onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>R$</Typography>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Data da Despesa"
                  fullWidth
                  required
                  type="date"
                  value={expenseForm.expense_date}
                  onChange={(e) => setExpenseForm({ ...expenseForm, expense_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Método de Pagamento"
                  fullWidth
                  value={expenseForm.payment_method}
                  onChange={(e) => setExpenseForm({ ...expenseForm, payment_method: e.target.value })}
                  placeholder="Ex: Cartão, PIX"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="URL do Comprovante"
                  fullWidth
                  value={expenseForm.receipt_url}
                  onChange={(e) => setExpenseForm({ ...expenseForm, receipt_url: e.target.value })}
                  placeholder="https://..."
                />
              </Grid>
            </Grid>

            <FormControlLabel
              control={
                <Checkbox
                  checked={expenseForm.is_recurring}
                  onChange={(e) => setExpenseForm({ ...expenseForm, is_recurring: e.target.checked })}
                />
              }
              label="Despesa Recorrente"
            />

            {expenseForm.is_recurring && (
              <FormControl fullWidth>
                <InputLabel>Frequência</InputLabel>
                <Select
                  value={expenseForm.recurring_frequency}
                  onChange={(e) => setExpenseForm({ ...expenseForm, recurring_frequency: e.target.value })}
                  label="Frequência"
                >
                  <MenuItem value="monthly">Mensal</MenuItem>
                  <MenuItem value="yearly">Anual</MenuItem>
                  <MenuItem value="weekly">Semanal</MenuItem>
                  <MenuItem value="daily">Diária</MenuItem>
                </Select>
              </FormControl>
            )}

            <TextField
              label="Notas"
              fullWidth
              multiline
              rows={3}
              value={expenseForm.notes}
              onChange={(e) => setExpenseForm({ ...expenseForm, notes: e.target.value })}
              placeholder="Observações adicionais..."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>
            {editingExpense ? 'Atualizar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FinancialControlExpenses;


